/**
 * The one place a view id and its URL are related.
 *
 * The app previously had no router at all — navigation was a single useState
 * string in Dashboard. That meant no deep links, the browser Back button exited
 * the app, a refresh always dumped you back on Today, and the two links the
 * platform emails out (`/payment-approval` and `/auth/reset-password`) pointed
 * at routes that did not exist.
 */

export const VIEW_PATHS: Record<string, string> = {
  today: '/today',
  smart_inbox: '/smart-inbox',
  sourced: '/sourcing',
  pipeline: '/pipeline',
  contracts: '/contracts',
  sale_details: '/sale-details',
  stock: '/stock',
  cost_of_sales: '/cost-of-sales',
  commission_recon: '/commission-report',
  standard_costs: '/standard-costs',
  dd_review: '/dd-review',
  payment_run: '/payment-run',
  payment_approval: '/payment-approval',
  gst_register: '/gst-register',
  sales_ar: '/sales-ar',
  ar_reminders: '/ar-reminders',
  sms_log: '/sms-log',
  commission_admin: '/commissions',
  reports: '/reports',
  feedback: '/feedback',
  settings: '/admin',
}

export const PATH_VIEWS: Record<string, string> = Object.fromEntries(
  Object.entries(VIEW_PATHS).map(([view, path]) => [path, view])
)

export const VIEW_LABELS: Record<string, string> = {
  today: 'Today',
  sourced: 'Autograb Sourcing',
  pipeline: 'Deal Pipeline',
  contracts: 'Purchase Details (Workspace)',
  sale_details: 'Sale Details (Workspace)',
  stock: 'Stock Management',
  cost_of_sales: 'Cost of Sales',
  commission_recon: 'Commission Report',
  standard_costs: 'Standard Costs',
  sms_log: 'SMS Log',
  dd_review: 'DD Review',
  payment_run: 'Payment Run (AP)',
  payment_approval: 'Payment Approval',
  gst_register: 'Margin Scheme (GST Register)',
  sales_ar: 'Sales (AR)',
  ar_reminders: 'AR Reminders',
  smart_inbox: 'Smart Inbox',
  reports: 'Reports',
  commission_admin: 'Commissions Settings',
  feedback: 'Feedback',
  settings: 'Admin',
}

export const pathForView = (view: string): string => VIEW_PATHS[view] || '/today'

/** Which view a pathname belongs to. Unknown paths fall back to Today. */
export const viewForPath = (pathname: string): string => {
  const clean = pathname.replace(/\/+$/, '') || '/today'
  if (PATH_VIEWS[clean]) return PATH_VIEWS[clean]
  // A deal deep link (/deals/:id) is shown over the pipeline.
  if (clean.startsWith('/deals/')) return 'pipeline'
  return 'today'
}

/** Deep link to a single deal, openable from an email or a colleague's message. */
export const pathForDeal = (dealId: string): string => `/deals/${dealId}`

export const dealIdFromPath = (pathname: string): string | null => {
  const m = /^\/deals\/([^/?#]+)/.exec(pathname)
  return m ? m[1] : null
}
